import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import {
  CtVtRating,
  DtMeterType,
  getInitDtMeterType,
} from 'src/app/models/dtMeterType.model';
import { PermissionsEnum } from 'src/app/models/role.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { DtMeterTypeService } from 'src/app/services/dt-meter-type.service';
import { PermissionsService } from 'shared-lib';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';

interface MultiSelectItem {
  id: number;
  name: string;
  checked: boolean;
  translation?: string;
}

@Component({
  selector: 'crud-dt-meter-type',
  templateUrl: './crud-dt-meter-type.component.html',
  styleUrls: ['./crud-dt-meter-type.component.scss'],
})
export class CrudDtMeterTypeComponent implements OnInit {
  public PermissionsEnum = PermissionsEnum;
  @Input() searchText!: string;
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;

  public currentPage: number = 1;
  public pageSize: number = 5;
  public count: number = 0;

  dtMeterTypes: DtMeterType[] = [];
  selectedDtMeterType: DtMeterType = getInitDtMeterType();
  actionLabel$!: Observable<string>;
  isEditMode: boolean = false;
  @ViewChild('closeAddModal') closeAddModal: ElementRef;
  @ViewChild('closeDeleteModal') closeDeleteModal: ElementRef;

  ratingItems: MultiSelectItem[] = [
    { id: 1, name: 'CT', checked: false },
    { id: 2, name: 'VT', checked: false },
    { id: 3, name: 'NONE', checked: false, translation: 'COMMON.NONE' },
  ];
  ratingClicked: boolean = false;

  constructor(
    private dtMeterTypeService: DtMeterTypeService,
    public permissionsService: PermissionsService,
    private toastr: ToastrService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.utilityId != 0) {
      this.reloadTable();
    }

    this.updateActionLabel();
  }

  private updateActionLabel(): void {
    const key = this.isEditMode ? TranslationUtils.EDIT_KEY : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  addNew() {
    this.isEditMode = false;
    this.selectedDtMeterType = getInitDtMeterType();
    this.ratingItems.forEach((x) => (x.checked = false));
    this.ratingClicked = !this.ratingClicked;
    this.updateActionLabel();
  }

  selectDtMeterType(dtMeterType: DtMeterType) {
    this.isEditMode = true;
    this.selectedDtMeterType = { ...dtMeterType };
    this.ratingItems.forEach(
      (x) => (x.checked = dtMeterType.rating.includes(x.name as CtVtRating))
    );
    this.ratingClicked = !this.ratingClicked;
    this.updateActionLabel();
  }

  onRatingSelectionChange(selected: { checked: boolean; name: string; id: number }) {
    if (selected.name === 'NONE') {
      if (selected.checked) {
        this.ratingItems.forEach((x) => {
          if (x.name !== 'NONE') {
            x.checked = false;
          }
        });
      }
    } else if (selected.checked) {
      const noneItem = this.ratingItems.find((x) => x.name === 'NONE');
      if (noneItem) {
        noneItem.checked = false;
      }
    }
    this.ratingClicked = !this.ratingClicked;
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable(this.searchText);
  }

  reloadTable(searchText?: string, page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllDtMeterTypes(dataIn);
  }

  filter() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllDtMeterTypes(dataIn);
  }

  private getAllDtMeterTypes(dataIn: any) {
    this.dtMeterTypeService.getAllDtMeterTypes(dataIn).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.dtMeterTypes = responseData.data.data;
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  save() {
    if (!this.ratingItems.some((x) => x.checked)) {
      this.toastr.warning(
        this.translateService.instant('COMMON.DT_METER_TYPE.RATING_REQUIRED')
      );
      return;
    }

    this.selectedDtMeterType.utilityId = this.utilityId;
    this.selectedDtMeterType.rating = this.ratingItems
      .filter((x) => x.checked && x.name !== 'NONE')
      .map((x) => x.name as CtVtRating);
    this.dtMeterTypeService
      .saveDtMeterType(this.selectedDtMeterType)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.closeAddModal?.nativeElement?.click();
            this.toastr.success(responseData?.message);
            if (this.selectedDtMeterType.id === 0) {
              this.pageChange(1);
              return;
            }
            this.reloadTable(this.searchText);
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  deleteDtMeterType() {
    this.dtMeterTypeService
      .deleteDtMeterType(this.selectedDtMeterType.id)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          this.closeDeleteModal?.nativeElement?.click();
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.pageChange(1);
            this.toastr.success(responseData?.message);
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  ratingLabel(rating: CtVtRating[]): string {
    return rating.join('/');
  }
}
